import type {BarcodeScanner, ScannerSettings} from './barcode-scanner.types';
import { ScannerState} from './barcode-scanner.types';
import type {Observable} from 'rxjs';
import {BehaviorSubject, Subject, switchMap, window} from 'rxjs';
import type {Barcode} from './barcode';
import {validateResults} from './utils';
import {tap} from 'rxjs/operators';
import {shareWithRefCount} from '../utils/observables-utils';

/**
 * @name StreamBarcodeScanner
 * @module portaro.barcode-scanner
 * @kind class
 *
 * @description
 * Barcode scanner manages barcode scanning (esp. result validation/filtering)
 * Image sampling and decoding is delegated to {@Link BarcodeScanner}
 * It outputs stream of barcodes.
 */
export class StreamBarcodeScanner {
    private state$: BehaviorSubject<ScannerState>;
    private scannedBarcode$: Subject<Barcode>; // raw unprocessed unfiltered scan results
    private processedBarcode$: Observable<Barcode>; // filtered validated barcodes
    private isScanActive: boolean;
    private readonly VALIDATION_BUFFER_SIZE: number = 3;
    private clearBufferSignal$: Subject<void> = new Subject();

    constructor(private barcodeScanner: BarcodeScanner, private scanSettings: ScannerSettings) {

        this.isScanActive = false;
        this.resetBarcodeStream();
        this.state$ = new BehaviorSubject(ScannerState.READY_TO_OPERATE);
    }

    public isActive(): boolean {
        return this.isScanActive;
    }

    public getState(): ScannerState {
        return this.state$.value;
    }

    public getState$(): Observable<ScannerState> {
        return this.state$.asObservable();
    }

    public scan(): Observable<Barcode> {
        return this.processedBarcode$;
    }

    public stopScanning(): void {
        if (this.state$.value !== ScannerState.READY_TO_OPERATE) { // state already reset, without any subscribers (complete wont trigger stream init)
            this.scannedBarcode$.complete();
        }
    }

    public clearValidationBuffer(): void {
        this.clearBufferSignal$.next();
    }

    private resetScanner() {
        this.isScanActive = false;
        this.barcodeScanner.stopScanner();
        this.state$.next(ScannerState.READY_TO_OPERATE);
    }

    // creates new stream
    private resetBarcodeStream() {
        this.scannedBarcode$ = new Subject<Barcode>();
        this.processedBarcode$ = this.scannedBarcode$.pipe(
            window(this.clearBufferSignal$), // on signal creates new nested observable from source (each nested observable gets its own validation buffer with switchMap further down this pipe)
            switchMap((windowedChunk$) => windowedChunk$.pipe(validateResults(this.scanSettings.allowEmptyResult, this.VALIDATION_BUFFER_SIZE))), // subscribes to the latest chunk$ and validates results from it (uses buffer)
            tap({
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                subscribe: () => this.onStreamStart(),
                complete: () => this.resetBarcodeStream(),
                finalize: () => this.onStreamStop(),
            }),
            shareWithRefCount());
    }

    private async startScanner() {
        try {
            await this.barcodeScanner.startScanner((result) => this.processResult(result));

            if (this.state$.value === ScannerState.STARTING) { // successful scanner start
                this.state$.next(ScannerState.OPERATING);
            } else { // scanner was stopped before scanner fully started
                this.resetScanner(); // reinitialize scanner (shut down camera)
            }

        } catch (error) {
            this.state$.next(ScannerState.ERRORED);
            this.isScanActive = false;
            this.scannedBarcode$.error(error);
        }

        return;
    }

    private async onStreamStart() {
        this.isScanActive = true;
        const previousState = this.state$.value;
        this.state$.next(ScannerState.STARTING);

        if (previousState === ScannerState.READY_TO_OPERATE) {
            await this.startScanner();
        }
    }

    private onStreamStop() {
        if (this.state$.value === ScannerState.STARTING) {
            this.state$.next(ScannerState.WAITING);
        } else {
            this.resetScanner();
        }
    }

    private processResult(result: Barcode) {
        this.scannedBarcode$.next(result);
    }
}